// src/pages/PerformanceTracker.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tutorialAttemptAPI, studentTutorialAPI } from '../services/api';
import { io } from 'socket.io-client';
import { socketAuth } from '../utils/socketAuth';
import ReactMarkdown from 'react-markdown';
import './PerformanceTracker.css';

const SOCKET_SERVER_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// +++ HELPER: Flatten MCQs from Object to Array +++
// This fixes the crash where the code expects an array but gets { easy: [], medium: [], hard: [] }
const flattenMcqs = (chunk) => {
    if (!chunk || !chunk.mcqs) return [];
    // If it's already an array (legacy data), return it
    if (Array.isArray(chunk.mcqs)) return chunk.mcqs;
    
    // Otherwise, flatten the difficulty buckets
    return [
        ...(chunk.mcqs.easy || []),
        ...(chunk.mcqs.medium || []),
        ...(chunk.mcqs.hard || [])
    ];
};

function PerformanceTracker() {
  const { tutorialId, studentId } = useParams();
  
  const [attempt, setAttempt] = useState(null); 
  const [tutorial, setTutorial] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const socketRef = useRef();

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        const tutorialRes = await studentTutorialAPI.getById(tutorialId);
        setTutorial(tutorialRes.data);

        // Always fetches the most recent attempt from the DB
        const attemptRes = await tutorialAttemptAPI.getTutorialProgress(tutorialId, studentId);
        setAttempt(attemptRes.data);

      } catch (err) {
        console.error("Error fetching data:", err);
        if (err.response && err.response.status === 404) {
          setAttempt({ answers: [] }); 
        } else {
          setError('Failed to load tracking data.');
        }
      }
      setLoading(false);
    };
    fetchInitialData();
  }, [tutorialId, studentId]);

  useEffect(() => {
    // Ensure we connect to the root URL (removing /api if present)
    // Teacher's ID token in the handshake: the server only lets the tutorial's owner join its room.
    socketRef.current = io(SOCKET_SERVER_URL.replace('/api', ''), { auth: socketAuth }); 
    
    socketRef.current.emit('join-tutorial-room', tutorialId);
    console.log(`Socket joined room: ${tutorialId}`);
    
    socketRef.current.on('new-answer', (newAnswerData) => {
      // +++ FIX: Robust String Comparison for IDs +++
      // Ensure both are strings to prevent mismatch between ObjectID and String
      if (String(newAnswerData.studentId) === String(studentId)) {
        
        setAttempt((prevAttempt) => {
            // +++ ROBUST FIX: Use Database ID (attemptId) for change detection +++
            // If the incoming answer belongs to a different Attempt Document ID than what we are showing,
            // it is 100% a new session.
            const incomingAttemptId = newAnswerData.attemptId;
            const currentAttemptId = prevAttempt?._id;

            const isNewSession = prevAttempt && 
                                 currentAttemptId && 
                                 incomingAttemptId && 
                                 incomingAttemptId !== currentAttemptId;

            if (isNewSession) {
                console.log(`New session detected (ID: ${incomingAttemptId}). Resetting tracker.`);
                return {
                    ...prevAttempt, // Keep student/tutorial metadata
                    _id: incomingAttemptId,
                    clientSessionId: newAnswerData.clientSessionId,
                    answers: [newAnswerData.answer] // Start fresh list
                };
            }

            // Otherwise, append normally
            const updatedAnswers = prevAttempt ? [...prevAttempt.answers, newAnswerData.answer] : [newAnswerData.answer];
            
            return {
                ...prevAttempt,
                _id: prevAttempt?._id || newAnswerData.attemptId, 
                clientSessionId: prevAttempt?.clientSessionId || newAnswerData.clientSessionId,
                answers: updatedAnswers
            };
        });
      }
    });

    return () => {
      console.log('Socket disconnecting...');
      socketRef.current.disconnect();
    };
  }, [tutorialId, studentId]); 

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  // +++ FIX: Use helper to calculate total questions +++
  const totalQuestions = tutorial ? tutorial.generatedContent.reduce((acc, chunk) => acc + flattenMcqs(chunk).length, 0) : 0;
  const totalAnswered = attempt ? attempt.answers.length : 0;
  const score = attempt ? attempt.answers.filter(a => a.isCorrect).length : 0;

  return (
    <div className="performance-tracker-split-screen">
      
      {/* Left Side: Tutorial Preview */}
      <div className="split-pane preview-pane">
        <h2>Tutorial Preview</h2>
        {tutorial ? (
          <>
            <h3>{tutorial.title}</h3>
            <p>For: {tutorial.studentId?.name || tutorial.studentName}</p>
            <hr />
            {tutorial.generatedContent.map((chunk, chunkIndex) => {
              // +++ FIX: Flatten MCQs for display +++
              const flattenedMcqs = flattenMcqs(chunk);
              
              return (
                <div key={chunkIndex} className="preview-chunk">
                  <h4>Chunk {chunkIndex + 1}</h4>
                  {flattenedMcqs.map((mcq, qIndex) => (
                    <div key={qIndex} className="preview-card">
                      {/* +++ FIX: Render Question +++ */}
                      <div style={{fontWeight:'bold', marginBottom:'5px'}}>
                          {flattenedMcqs.length * chunkIndex + qIndex + 1}. <ReactMarkdown components={{ p: 'span' }}>{mcq.question}</ReactMarkdown>
                      </div>
                      {/* +++ FIX: Render Answer +++ */}
                      <div style={{ color: 'green' }}>
                          Correct: <ReactMarkdown components={{ p: 'span' }}>{mcq.correctAnswer}</ReactMarkdown>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        ) : (
          <p>Loading tutorial content...</p>
        )}
      </div>
      
      {/* Right Side: Live Answer Tracker */}
      <div className="split-pane tracker-pane">
        <h2>Live Progress ({totalAnswered} / {totalQuestions} Answered)</h2>
        <p><strong>Score: {score}</strong></p>
        <Link to={`/student-tutorial-player/${tutorialId}`}>Open Student Preview</Link>
        <hr />
        {attempt && attempt.answers.length > 0 ? (
          [...attempt.answers].reverse().map((ans, index) => ( // +++ FIX: Show newest first +++
            <div key={index} className="answer-card" 
                 style={{ borderColor: ans.isCorrect ? '#28a745' : '#dc3545' }}>
              {/* +++ FIX: Render Question +++ */}
              <h4>Q: <ReactMarkdown components={{ p: 'span' }}>{ans.questionText}</ReactMarkdown></h4>
              
              <div style={{ color: ans.isCorrect ? '#28a745' : '#dc3545' }}>
                <strong>Student:</strong> <ReactMarkdown components={{ p: 'span' }}>{ans.studentAnswer}</ReactMarkdown>
              </div>
              
              {!ans.isCorrect && (
                <div style={{marginTop:'5px'}}>
                    <strong>Correct:</strong> <ReactMarkdown components={{ p: 'span' }}>{ans.correctAnswer}</ReactMarkdown>
                </div>
              )}
            </div>
          ))
        ) : (
          <p>Waiting for the first answer...</p>
        )}
      </div>

    </div>
  );
}

export default PerformanceTracker;